import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { InlineSvgComponent } from '../../components/inline-svg/inline-svg.component';
import { RiskReport } from '../../models/report.model';
import {
  TaskGroup,
  TaskStatistic,
  TaskStatus,
  taskStatusTranslations
} from '../../models/tasks.model';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { NotificationService } from '../../services/notification.service';
import { TaskService } from '../../services/task.service';

@Component({
  selector: 'frmg-download-table',
  templateUrl: './download-table.component.html',
  styleUrls: ['./download-table.component.scss'],
  imports: [
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatTooltip,
    NgClass,
    MatButton,
    InlineSvgComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatDivider,
    MatPaginator,
    DatePipe,
    TranslateModule,
    TruncatePipe
  ]
})
export class DownloadTableComponent implements OnInit, OnDestroy {
  public displayedColumns = [
    'area',
    'total_aois',
    'created_at',
    'status',
    'action'
  ];
  public taskData: Array<TaskGroup> = [];
  public currentPage = 1;
  public pageSize = 100;
  public totalElements = 0;
  public statistic: Partial<TaskStatistic> = {
    completed: 0,
    started: 0,
    cancelled: 0,
    failed: 0
  };
  public taskStatusTranslations = taskStatusTranslations;
  private updateTimeout;

  constructor(
    private taskService: TaskService,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.getTasks();
  }

  ngOnDestroy(): void {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
  }

  public getTasks(): void {
    this.taskService
      .getTasksGroup(this.currentPage, this.pageSize)
      .subscribe((result) => {
        this.totalElements = result.count;
        this.taskData = result.results;
        if (
          result.results.some(
            (task) =>
              task.status === TaskStatus.PROGRESS ||
              task.status === TaskStatus.PENDING ||
              task.status === TaskStatus.STARTED
          )
        ) {
          this.updateTimeout = setTimeout(() => this.getTasks(), 3000);
        }
      });
  }

  public download(element: TaskGroup, event: MouseEvent): void {
    event.stopPropagation();
    if (element.url) {
      window.open(element.url, '_blank');
    } else {
      this.taskService
        .prepareDownloadTasksGroupById(element.id)
        .subscribe((task) => {
          this.downloadTask(element, task);
        });
    }
  }

  private downloadTask(element: TaskGroup, task) {
    this.notificationService.updateDownload({
      fileName: element.name,
      percentage: 0,
      total: 0,
      loaded: 0
    });
    this.taskService
      .downloadTasksGroupById(element.id, task.task_id, element.name)
      .subscribe((resp) => {
        this.notificationService.updateDownload({
          fileName: element.name,
          percentage: resp.progress,
          total: 0,
          loaded: 0
        });
      });
  }

  public deleteDownload(element: TaskGroup, event: MouseEvent) {
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: 'Are you sure you want to delete this file?',
          body: 'Once deleted, it cannot be recovered.',
          confirmText: 'Proceed'
        }
      })
      .afterClosed()
      .subscribe((del) => {
        if (del) {
          this.taskService
            .deleteTasksGroupById(element.id)
            .subscribe((_resp) => {
              const index = this.taskData.findIndex(
                (el) => el.id == element.id
              );
              if (index !== -1) {
                this.taskData.splice(index, 1);
                this.taskData = this.taskData.map((item) => item);
                this.cd.detectChanges();
              }
            });
        }
      });

    event.stopPropagation();
  }

  public handlePageEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.currentPage = e.pageIndex + 1;
    this.getTasks();
  }

  public statusClass(report: RiskReport) {
    switch (report.status) {
      case 'completed':
        return 'icon icon-completed';
      case TaskStatus.PROGRESS:
      case TaskStatus.PENDING:
      case TaskStatus.STARTED:
        return 'icon icon-processing';
      default:
        return 'icon icon-completed';
    }
  }

  public goToTask(task: TaskGroup) {
    this.router.navigate([`/downloads/${task.id}`]);
  }
}
